import { Component, OnInit } from "@angular/core";
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule,
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
} from "@angular/forms";
import { Router, RouterLink, ActivatedRoute } from "@angular/router";

// Make router public for template access
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatIconModule } from "@angular/material/icon";
import { MatCheckboxModule } from "@angular/material/checkbox";

import { AuthService } from "../../../core/services/auth.service";
import { RegisterRequest } from "../../../core/models/auth.model";

@Component({
    selector: "app-register",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterLink,
        MatButtonModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatIconModule,
        MatCheckboxModule,
    ],
    templateUrl: "./register.html",
    styleUrls: ["./register.scss"],
})
export class RegisterComponent implements OnInit {
    registerForm: FormGroup;
    error: string = "";
    loading: boolean = false;
    inviteCode: string = "";
    hidePassword = true;
    hideConfirmPassword = true;
    currentYear: number = new Date().getFullYear();
    showSuccessMessage: boolean = false;
    registeredEmail: string = "";
    inviteLoading: boolean = false;
    inviteAccepted: boolean = false;

    constructor(
        private fb: FormBuilder,
        private authService: AuthService,
        public router: Router,
        private route: ActivatedRoute
    ) {
        this.registerForm = this.fb.group({
            firstName: ["", [Validators.required]],
            lastName: ["", [Validators.required]],
            email: ["", [Validators.required, Validators.email]],
            password: ["", [
                Validators.required,
                Validators.minLength(8),
                Validators.maxLength(128),
                this.passwordComplexityValidator()
            ]],
            confirmPassword: ["", [Validators.required]],
        }, { validators: this.passwordMatchValidator });
    }

    passwordComplexityValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;
            if (!value) return null;

            const hasUpperCase = /[A-Z]/.test(value);
            const hasNumber = /[0-9]/.test(value);
            const hasSymbol = /[^A-Za-z0-9]/.test(value);

            const passwordValid = hasUpperCase && hasNumber && hasSymbol;

            if (!passwordValid) {
                return {
                    complexity: {
                        hasUpperCase,
                        hasNumber,
                        hasSymbol
                    }
                };
            }
            return null;
        };
    }

    passwordMatchValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
        const password = group.get('password')?.value;
        const confirmPassword = group.get('confirmPassword')?.value;
        const confirmControl = group.get('confirmPassword');

        if (password !== confirmPassword) {
            confirmControl?.setErrors({ ...confirmControl.errors, mismatch: true });
            return { mismatch: true };
        } else {
            if (confirmControl?.hasError('mismatch')) {
                const errors = { ...confirmControl.errors };
                delete errors['mismatch'];
                confirmControl.setErrors(Object.keys(errors).length ? errors : null);
            }
            return null;
        }
    };

    ngOnInit(): void {
        this.route.queryParams.subscribe((params) => {
            const code = params["code"];
            if (code) {
                this.inviteCode = code;
                this.loadInvite(code);
            }
        });
    }

    // Prefills and locks the email field to the address the invite was sent to
    private loadInvite(code: string): void {
        this.inviteLoading = true;
        this.authService.getInviteDetails(code).subscribe({
            next: (invite) => {
                const emailControl = this.registerForm.get("email");
                emailControl?.setValue(invite.email);
                emailControl?.disable();
                this.inviteLoading = false;
            },
            error: (err) => {
                this.error = err.message;
                this.inviteLoading = false;
            },
        });
    }

    onSubmit(): void {
        if (this.registerForm.valid) {
            this.loading = true;
            this.error = "";

            // getRawValue so the locked (disabled) invite email is included
            const formValue = this.registerForm.getRawValue();
            const registerData: RegisterRequest = {
                name: formValue.firstName,
                surname: formValue.lastName,
                email: formValue.email,
                password: formValue.password,
            };

            if (this.inviteCode) {
                registerData.invite_code = this.inviteCode;
            }

            this.authService.register(registerData).subscribe({
                next: (response) => {
                    this.registeredEmail = registerData.email;
                    this.inviteAccepted = response?.email_verified === true || response?.code === "ROLE_UPGRADED";
                    this.showSuccessMessage = true;
                    this.loading = false;
                },
                error: (err) => {
                    this.error = err.message || "An error occurred during registration. Please try again.";
                    this.loading = false;
                },
            });
        }
    }
}
